"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Building2, CalendarCheck, CircleCheck as CheckCircle2, ChevronRight, FileCheck, HardHat, ShieldCheck, TimerReset } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const sitePhotosQuery = new WP_Query({
  post_type: "site_photo",
  per_page: 20
});
function RelationshipImage() {
  const {
    posts,
    loading
  } = usePosts();
  if (loading) {
    return <div className="w-full h-full bg-muted animate-pulse" />;
  }
  const photo = posts?.find(p => p.title?.toLowerCase().includes("main contractors developers relationship") || p.customFields?.photo_name?.toLowerCase().includes("main contractors developers relationship"));
  const imageUrl = photo?.customFields?.image || photo?.featuredImage;
  const altText = photo?.customFields?.alt_text || "Commercial scaffolding setup on multi-storey project by BMF Scaffolding";
  if (imageUrl) {
    return <img src={imageUrl} data-wvc-srcset="" data-wvc-sizes="" alt={altText} loading="lazy" decoding="async" className="w-full h-full object-cover" />;
  }
  return <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/commercial_scaffold_relationship_project.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/commercial_scaffold_relationship_project.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/commercial_scaffold_relationship_project.webp-wvc-sizes" alt="Commercial scaffolding setup on multi-storey project by BMF Scaffolding" loading="lazy" decoding="async" className="w-full h-full object-cover" />;
}
const partnershipPillars = [{
  icon: TimerReset,
  title: "Reliable handover schedules",
  description: "Multi-phase erection and strike timetables structured around main contractor trades to prevent site delays."
}, {
  icon: ShieldCheck,
  title: "Audited safety compliance",
  description: "NASC and CHAS accredited setups with TG20 compliant calculations and handover handover certificates."
}, {
  icon: CalendarCheck,
  title: "Statutory 7-day inspections",
  description: "Scheduled safety audits, handover tags, and transparent digital reporting throughout the project lifecycle."
}];
const relationshipHighlights = [{
  title: "Framework maintenance & commercial roofing",
  detail: "Over 3 years of ongoing phased access for regional roofing contractors across Berkshire and Surrey.",
  tag: "Commercial partnership"
}, {
  title: "Phased town centre development",
  detail: "Full perimeter independent scaffolding with public protection fans, pavement licensing, and progressive strikes.",
  tag: "Main contractor access"
}];
export default function Relationship() {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="longtermrelationshipcase" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4 bg-muted/50 text-foreground border-border">
            Long-term partnership case
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-4">
            Building partnerships that last across the South East
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            BMF Scaffolding works alongside main contractors, roofing specialists, and local councils to provide dependable, safety-led scaffolding across multi-month frameworks and phased builds.
          </p>
        </div>

        {/* Main Case Study Feature Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch mb-12">
          
          {/* Left Column: Case Narrative & Image */}
          <motion.div initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeIn} className="lg:col-span-7 flex flex-col justify-between">
            <Card className="h-full border border-border bg-card shadow-sm flex flex-col overflow-hidden">
              <div className="relative w-full h-64 sm:h-72 bg-muted overflow-hidden">
                <PostsProvider wp_query={{
                post_type: "site_photo",
                per_page: 20,
                status: ["publish"]
              }}>
                  <RelationshipImage />
                </PostsProvider>
                <div className="absolute top-4 left-4">
                  <Badge className="bg-secondary text-secondary-foreground shadow-xs">
                    Featured case study
                  </Badge>
                </div>
              </div>

              <CardHeader className="pb-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <Building2 className="w-4 h-4 text-primary" />
                  <span>Commercial contractor framework • Berkshire & Surrey</span>
                </div>
                <CardTitle className="text-2xl font-bold text-card-foreground">
                  Multi-site commercial access management
                </CardTitle>
                <CardDescription className="text-base text-muted-foreground mt-2">
                  Operating as the primary scaffolding contractor for regional property portfolios, delivering tailored access solutions from initial site surveys through to scheduled progressive dismantling.
                </CardDescription>
              </CardHeader>

              <CardContent className="pt-2 flex-1 flex flex-col justify-between gap-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-3.5 rounded-lg bg-muted/40 border border-border">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Project scope</p>
                    <p className="text-sm font-medium text-foreground">Independent access, loading bays & edge protection</p>
                  </div>
                  <div className="p-3.5 rounded-lg bg-muted/40 border border-border">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Compliance level</p>
                    <p className="text-sm font-medium text-foreground">NASC compliant, full TG20 design certification</p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-4 pt-4 border-t border-border">
                  <Button asChild variant="default" size="sm">
                    <Link to="/ProjectsCaseStudiesPage" className="inline-flex items-center gap-1.5">
                      <span>View all projects</span>
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="sm">
                    <Link to="/CommercialScaffoldingPage">
                      Commercial scaffolding details
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Right Column: Key Operational Pillars */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <Card className="border border-border bg-card shadow-sm p-2">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2 text-primary mb-1">
                  <HardHat className="w-5 h-5" />
                  <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Operational standard</span>
                </div>
                <CardTitle className="text-xl font-bold text-card-foreground">
                  How we manage long-term accounts
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-5 pt-1">
                {partnershipPillars.map((pillar, i) => {
                const Icon = pillar.icon;
                return <div key={pillar.title} data-index={i} className="flex gap-4 items-start">
                      <div className="p-2.5 rounded-lg bg-accent text-accent-foreground shrink-0 mt-0.5 border border-border">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground mb-1">
                          {pillar.title}
                        </h4>
                        <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                          {pillar.description}
                        </p>
                      </div>
                    </div>;
              })}
              </CardContent>
            </Card>

            {/* Accreditation & Supervision Card */}
            <Card className="border border-border bg-muted/40 shadow-xs p-2">
              <CardContent className="pt-4 flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <FileCheck className="w-5 h-5 text-primary shrink-0" />
                  <span className="text-sm font-semibold text-foreground">
                    Accredited standards on every site
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Led by Pete Wood with 20+ years of operational scaffolding experience. All teams operate strictly under NASC and CHAS protocols.
                </p>
                <div className="flex items-center gap-3 pt-2 text-xs font-medium text-foreground">
                  <span className="inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary" /> NASC
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary" /> CHAS
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Secondary Relationship Scenarios */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {relationshipHighlights.map((item, i) => <div key={item.title} data-index={i} className="p-6 rounded-xl bg-card border border-border shadow-xs flex flex-col justify-between gap-4">
              <div>
                <Badge variant="secondary" className="mb-3 text-xs">
                  {item.tag}
                </Badge>
                <h4 className="text-lg font-bold text-foreground mb-2">
                  {item.title}
                </h4>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {item.detail}
                </p>
              </div>
              <div className="pt-2">
                <Button asChild variant="link" className="px-0 h-auto text-primary font-semibold text-sm">
                  <Link to="/CommercialScaffoldingPage" className="inline-flex items-center gap-1">
                    <span>Explore commercial services</span>
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>)}
        </div>

        <Separator className="my-10" />

        {/* Call to Action Bar */}
        <div className="rounded-2xl bg-secondary text-secondary-foreground p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6 shadow-md">
          <div className="max-w-xl text-center md:text-left">
            <h3 className="text-2xl font-bold mb-2">
              Discuss your upcoming scaffolding requirements
            </h3>
            <p className="text-sm sm:text-base text-secondary-foreground/85">
              Talk directly with our team about commercial frameworks, multi-trade access schedules, or fixed-price site quotations.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ContactRequestQuotePage">
                Discuss a project
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="w-full sm:w-auto bg-transparent text-secondary-foreground border-secondary-foreground/30 hover:bg-secondary-foreground/10 hover:text-secondary-foreground">
              <Link to="/ContactRequestQuotePage">
                Request a quote
              </Link>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}